import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useBetting } from '../../../../../Context/BettingContext';
import { useHistory } from 'react-router-dom';
import { History } from 'lucide-react';
import BettingHistoryTable from '../../components/BettingHistoryTable';
import { useExposure } from '../../../../../hooks/useExposure';
import { API } from '../../../../../Helper';

// Styled Components (kept as you had, slightly normalized)
const Container = styled.div`
  width: 100%;
  background: #232626;
  font-family: 'Montserrat', sans-serif !important;
  margin-top: 5rem;
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  background: rgb(50, 55, 56);
`;

const TabButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? 'rgb(240, 156, 6)' : 'transparent'};
  color: ${props => props.active ? '#000' : '#fff'};
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 10px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: ${props => props.active ? 'rgb(240, 156, 6)' : 'rgba(240, 156, 6, 0.1)'};
    color: ${props => props.active ? '#000' : 'rgb(240, 156, 6)'};
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 100%; height: 2px;
    background-color: ${props => props.active ? 'rgb(240, 156, 6)' : 'transparent'};
    transition: all 0.2s ease;
  }
`;

const HistoryButton = styled.button`
  background: transparent;
  border: none;
  color: #cccccc;
  cursor: pointer;
  padding: 8px;
  margin-left: auto;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
  font-family: 'Montserrat Alternates', sans-serif;

  &:hover {
    color: rgb(240, 156, 6);
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  background: #292d2e;
  padding: 2px;
  border-radius: 0 0 8px 8px;
  display: flex;
  flex-direction: column;
`;

const TabContent = styled.div`
  overflow-y: auto;
  flex: 1;
  background: #292d2e;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: rgb(50, 55, 56); }
  &::-webkit-scrollbar-thumb {
    background: rgb(240, 156, 6);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgb(220, 136, 6);
  }
`;

const Match = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid rgb(50, 55, 56);
  background: #292d2e;
`;
const ExposureText = styled.span`
  color: rgb(240, 156, 6);
  margin-left: auto;
  margin-right: 4px;
  margin-top: 8px;
  font-size: 9px;
  font-weight: bold;
  font-family: 'Montserrat Alternates', sans-serif;
`;
const MatchInfo = styled.div` flex: 1; `;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px; height: 8px;
  background-color: ${props => props.isLive ? '#2ecc71' : '#95a5a6'};
  border-radius: 50%;
  margin-right: 8px;
  animation: ${props => props.isLive ? 'blink 1s ease-in-out infinite' : 'none'};
  @keyframes blink {
    0% { opacity: 0 } 50% { opacity: 1 } 100% { opacity: 0 }
  }
`;

const MatchTitle = styled.div`
  color: #fff;
  font-weight: bold;
  font-size: 11px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: rgb(240, 156, 6);
  }
`;

const Status = styled.div`
  color: ${props => props.status === 'In-Play'
    ? 'rgb(240, 156, 6)'
    : props.status === 'Suspend'
    ? '#e74c3c'
    : '#ccc'};
  font-size: 10px;
  font-weight: bold;
  margin-top: 12px;
`;

const IconGroup = styled.div`
  display: flex;
  gap: 4px;
  margin-left: 8px;
`;

const PTE = styled.div`
  color: #fff;
  font-size: 12px;
  margin-right: 10px;
`;

const LoaderWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #292d2e;
  padding: 24px;
`;

const EmptyState = styled.div`
  color: #ffffff;
  text-align: center;
  padding: 24px 12px;
`;

const LoaderSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: rgb(240, 156, 6);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

/* ----------------------
   Simple in-memory cache (stale-while-revalidate) + dedupe
   ---------------------- */
const cache = {
  data: new Map(),     // key -> { value, timestamp }
  inFlight: new Map(), // key -> Promise
};

const DEFAULT_TTL_MS = 10 * 1000; // 10s for endpoints
const ODDS_TTL_MS = 5 * 1000;     // shorter TTL for odds

async function fetchWithCache(key, fetcher, { ttl = DEFAULT_TTL_MS } = {}) {
  const now = Date.now();
  const cached = cache.data.get(key);
  // If in-flight, return that promise (dedupe). If there's cached value we still return fast using Promise.resolve
  const inFlight = cache.inFlight.get(key);
  if (inFlight) {
    // return the in-flight promise (it will resolve to the fresh value)
    return inFlight;
  }

  if (cached) {
    const age = now - cached.timestamp;
    if (age < ttl) {
      // fresh
      return Promise.resolve(cached.value);
    } else {
      // stale: trigger background refresh but return cached value immediately
      backgroundRefresh(key, fetcher, ttl);
      return Promise.resolve(cached.value);
    }
  }

  // no cached -> fetch and cache
  const p = (async () => {
    try {
      const v = await fetcher();
      cache.data.set(key, { value: v, timestamp: Date.now() });
      return v;
    } finally {
      cache.inFlight.delete(key);
    }
  })();

  cache.inFlight.set(key, p);
  return p;
}

function backgroundRefresh(key, fetcher, ttl = DEFAULT_TTL_MS) {
  if (cache.inFlight.get(key)) return cache.inFlight.get(key);
  const p = (async () => {
    try {
      const v = await fetcher();
      cache.data.set(key, { value: v, timestamp: Date.now() });
      return v;
    } catch (e) {
      console.error('Background refresh failed for', key, e);
      return null;
    } finally {
      cache.inFlight.delete(key);
    }
  })();
  cache.inFlight.set(key, p);
  return p;
}

/* ----------------------
   Utility: safeFetchJson
   ---------------------- */
async function safeFetchJson(url, init = {}) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Fetch ${url} failed: ${res.status} ${res.statusText} ${text}`);
  }
  return res.json();
}

/* ----------------------
   Component
   ---------------------- */
const MobileSportsUI = ({ tab: sportTab }) => {
  const { addBet } = useBetting();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [mobileMatches, setMobileMatches] = useState([]);
  const [activeTab, setActiveTab] = useState('in-play');
  const [showHistory, setShowHistory] = useState(false);
  const { exposure, totalExposure } = useExposure();
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setShowHistory(false);
  };

  const handleHistoryClick = () => {
    history.push('/sports/markets/history');
  };

  // Map of tab -> sportId (same as your mapping)
  const tabToSportId = {
    'cricket': '4',
    'tennis': '2',
    'soccer': '1',
    'american-football': '6423',
    'baseball': '7511',
    'basketball': '7522',
    'boxing': '6',
    'darts': '3503',
    'gaelic-games': '2152880',
    'golf': '3',
    'greyhound-racing': '4339',
    'horse-racing': '7',
    'mma': '26420387',
    'motorsport': '8',
    'rugby-league': '1477',
    'rugby-union': '5',
    'snooker': '6422'
  };

  // Cached odds fetcher (per market)
  const fetchMatchOddsCached = useCallback(async (matchId) => {
    const key = `odds:${matchId}`;
    return fetchWithCache(key, async () => {
      // network fetch
      try {
        const url = `https://bkk07.com/sports/market-odds?market_id=${matchId}`;
        const json = await safeFetchJson(url, { headers: { 'Content-Type': 'application/json' } });
        const marketOdds = Array.isArray(json) ? json[0] : json?.[0] ?? json;
        if (!marketOdds?.runners) return null;
        const runners = marketOdds.runners;
        const formatted = {
          one: { back: '--', lay: '--' },
          x: { back: '--', lay: '--' },
          two: { back: '--', lay: '--' }
        };
        if (runners.length === 2) {
          formatted.one = {
            back: runners[0].ex.availableToBack[0]?.price || '--',
            lay: runners[0].ex.availableToLay[0]?.price || '--'
          };
          formatted.two = {
            back: runners[1].ex.availableToBack[0]?.price || '--',
            lay: runners[1].ex.availableToLay[0]?.price || '--'
          };
        } else if (runners.length === 3) {
          formatted.one = {
            back: runners[0].ex.availableToBack[0]?.price || '--',
            lay: runners[0].ex.availableToLay[0]?.price || '--'
          };
          formatted.x = {
            back: runners[1].ex.availableToBack[0]?.price || '--',
            lay: runners[1].ex.availableToLay[0]?.price || '--'
          };
          formatted.two = {
            back: runners[2].ex.availableToBack[0]?.price || '--',
            lay: runners[2].ex.availableToLay[0]?.price || '--'
          };
        }
        return formatted;
      } catch (err) {
        console.error('fetchMatchOddsCached error', err);
        throw err;
      }
    }, { ttl: ODDS_TTL_MS });
  }, []);

  // Core: fetch the main markets (cached)
  const fetchAllMatches = useCallback(async () => {
    setIsLoading(true);
    try {
      let rawMarkets = [];

      if (activeTab === 'in-play') {
        if (sportTab === 'all-sports') {
          // fetch two aggregated endpoints via cache
          const [inPlayData, allMatchesData] = await Promise.all([
            fetchWithCache('allinplay', async () => safeFetchJson(`${API}/sports/allinplay`, { headers: { 'Content-Type': 'application/json' } }), { ttl: DEFAULT_TTL_MS }),
            fetchWithCache('allmatches', async () => safeFetchJson('https://bkk07.com/sports/all-matches', { headers: { 'Content-Type': 'application/json' } }), { ttl: DEFAULT_TTL_MS })
          ]);

          const collectMarkets = (arr) => Array.isArray(arr) ? arr.flatMap(s => s?.markets || []) : [];
          const inPlayMarkets = collectMarkets(inPlayData).map(m => ({ ...m, inplay: true }));
          const allMarkets = collectMarkets(allMatchesData);

          const map = new Map();
          inPlayMarkets.forEach(m => m?.marketId && map.set(m.marketId, m));
          allMarkets.forEach(m => { if (m?.marketId && !map.has(m.marketId)) map.set(m.marketId, m); });
          rawMarkets = Array.from(map.values());
        } else {
          // per-sport in-play (cached per sport id)
          const sportId = tabToSportId[sportTab];
          if (sportId) {
            const key = `inplay:sport:${sportId}`;
            const res = await fetchWithCache(key, async () => {
              const json = await safeFetchJson(`${API}/sports/inplayGameId/${sportId}`, { headers: { 'Content-Type': 'application/json' } });
              const games = json?.games || json || [];
              const markets = Array.isArray(games) ? games.flatMap(g => g?.markets || []) : [];
              return markets.map(m => ({ ...m, inplay: true }));
            }, { ttl: DEFAULT_TTL_MS });
            rawMarkets = res || [];
          } else {
            rawMarkets = [];
          }
        }
      } else {
        // today / tomorrow
        if (sportTab === 'all-sports') {
          const key = `matches-by-date:${activeTab}`; // activeTab = 'today' or 'tomorrow'
          const endpoint = activeTab === 'today' ? 'matches-by-date/today' : 'matches-by-date/tomorrow';
          const data = await fetchWithCache(key, async () => safeFetchJson(`https://bkk07.com/sports/${endpoint}`, { headers: { 'Content-Type': 'application/json' } }), { ttl: DEFAULT_TTL_MS });
          rawMarkets = Array.isArray(data) ? data.flatMap(s => s.markets || []) : [];
        } else {
          const sportId = tabToSportId[sportTab];
          if (sportId) {
            const key = `matches:${activeTab}:${sportId}`;
            const path = activeTab === 'today' ? `matches/today/${sportId}` : `matches/tomorrow/${sportId}`;
            const data = await fetchWithCache(key, async () => safeFetchJson(`https://bkk07.com/sports/${path}`, { headers: { 'Content-Type': 'application/json' } }), { ttl: DEFAULT_TTL_MS });
            rawMarkets = Array.isArray(data) ? data.flatMap(s => s?.markets || []) : (data?.markets || []);
          } else {
            rawMarkets = [];
          }
        }
      }

      // map rawMarkets → our UI shape, then fetch odds (odds have their own cache)
      const withOdds = await Promise.all(
        (rawMarkets || []).map(async (m) => {
          const utcDate = new Date(m.marketStartTime);
          const timeStr = new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).format(utcDate);

          const match = {
            id: m.marketId,
            runners: m.runners,
            team1: m.runners?.[0]?.runnerName || '',
            team2: m.runners?.[1]?.runnerName || '',
            status: (m?.inplay ? 'In-Play' : timeStr),
            isLive: Boolean(m?.inplay),
            time: timeStr,
            date: utcDate,
            isSuspended: m.stopBet,
            eventid: m.version,
            odds: { one: { back: '--', lay: '--' }, x: { back: '--', lay: '--' }, two: { back: '--', lay: '--' } }
          };

          try {
            const odds = await fetchMatchOddsCached(match.id);
            if (odds) match.odds = odds;
          } catch (err) {
            // keep default odds on error
          }
          return match;
        })
      );

      // sort: in-play first, then by start time
      withOdds.sort((a, b) => {
        if (a.isLive && !b.isLive) return -1;
        if (!a.isLive && b.isLive) return 1;
        return new Date(a.date) - new Date(b.date);
      });

      if (mountedRef.current) setMobileMatches(withOdds);
    } catch (err) {
      console.error('Error fetching matches:', err);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [activeTab, sportTab, fetchMatchOddsCached]);

  // initial load + interval refresh
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      await fetchAllMatches();
      if (cancelled) return;
    };
    run();

    // interval: call fetchAllMatches but that uses cached endpoints and S-W-R so it's cheap
    const interval = setInterval(() => {
      fetchAllMatches().catch(e => console.error('interval fetch error', e));
    }, 30_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [fetchAllMatches]);

  const handleMatchClick = (m) => {
    history.push({
      pathname: `/match/fullmarket/event/${m.id}`,
      state: {
        matchData: {
          ...m,
          matchTitle: `${m.team1} v ${m.team2}`
        }
      }
    });
  };

  // Optional: manual refresh to force network (clears caches for related keys)
  const manualRefresh = () => {
    // remove main keys so next fetch will do network
    cache.data.delete('allinplay');
    cache.data.delete('allmatches');
    // remove sport-specific keys
    Object.values(tabToSportId).forEach(id => {
      cache.data.delete(`inplay:sport:${id}`);
      cache.data.delete(`matches:today:${id}`);
      cache.data.delete(`matches:tomorrow:${id}`);
    });
    // and odds (optional - expensive to clear all odds; here we leave them)
    fetchAllMatches();
  };

  return (
    <Container>
      <TabContainer>
        <TabButton active={activeTab === 'in-play'} onClick={() => handleTabClick('in-play')}>
          In-Play
        </TabButton>
        <TabButton active={activeTab === 'today'} onClick={() => handleTabClick('today')}>
          Today
        </TabButton>
        <TabButton active={activeTab === 'tomorrow'} onClick={() => handleTabClick('tomorrow')}>
          Tomorrow
        </TabButton>
        <ExposureText>Exposure: {totalExposure}</ExposureText>
        <HistoryButton onClick={() => setShowHistory((prev) => !prev)}>
          <History size={20} />
        </HistoryButton>
      </TabContainer>

      {!showHistory && (
        <ContentContainer>
          {isLoading ? (
            <LoaderWrap>
              <LoaderSpinner />
            </LoaderWrap>
          ) : (
            <TabContent>
              {mobileMatches.length === 0 ? (
                <EmptyState>No matches found</EmptyState>
              ) : (
                mobileMatches.map((m, i) => (
                  <Match key={m.id || i}>
                    <MatchInfo>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IconGroup>
                          {['all-sports', 'cricket'].includes(sportTab) ? (
                            <img src='/assets/images/sports/cricket.png' style={{ width: '30px', height: '20px', objectFit: 'contain' }} />
                          ) : sportTab === 'tennis' ? (
                            <img src='/assets/images/sports/tennis.png' style={{ width: '30px', height: '20px', objectFit: 'contain' }} />
                          ) : sportTab === 'soccer' ? (
                            <img src='/assets/images/sports/soccer.png' style={{ width: '30px', height: '20px', objectFit: 'contain' }} />
                          ) : null}
                          <img src='/assets/images/sports/livestream.png' style={{ width: '20px', height: '20px' }} />
                          <img src='/assets/images/sports/watch.png' style={{ width: '20px', height: '20px' }} />
                        </IconGroup>
                        <Status status={m.status}>{m.status}</Status>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                        <StatusDot isLive={m.isLive} />
                        <MatchTitle onClick={() => handleMatchClick(m)}>
                          {m.team1} v {m.team2}
                        </MatchTitle>
                      </div>
                    </MatchInfo>
                    <PTE>{m.odds?.one?.back}/{m.odds?.one?.lay}</PTE>
                  </Match>
                ))
              )}
            </TabContent>
          )}
        </ContentContainer>
      )}

      {showHistory && <BettingHistoryTable />}

      {/* hidden manual refresh helper for dev (call from console if needed) */}
      <div style={{ display: 'none' }}>
        <button id="manualRefresh" onClick={manualRefresh}>manualRefresh</button>
      </div>
    </Container>
  );
};

export default MobileSportsUI;
